class FloatingVideoPlayer extends HTMLElement {
  constructor() {
    super();
    this.isExpanded = false;
    this.isPlaying = false;
    this.isMuted = true;
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    // Check session storage first
  if (sessionStorage.getItem("floatingVideoHidden") === "true") {
    this.classList.add("pi-hidden");
    return; // stop execution, don’t attach events or autoplay
  }
    this.attachEventListeners();
    this.autoplay();
  }

  attachEventListeners() {
    this.video = this.querySelector('.floating-video__video');
    this.playButton = this.querySelector('.floating-video__play-button');
    this.closeButton = this.querySelector('.floating-video__control-button--close');
    this.closeHideButton = this.querySelector('.floating-video__close-hide');
    this.replayButton = this.querySelector('.floating-video__control-button--replay');
    this.volumeButton = this.querySelector('.floating-video__control-button--volume');
    this.progressBar = this.querySelector('.floating-video__progress-bar');
    this.progress = this.querySelector('.floating-video__progress');
    this.timestampCurrent = this.querySelector('.floating-video__timestamp-current');
    this.timestampTotal = this.querySelector('.floating-video__timestamp-total');
    this.speedButton = this.querySelector('.floating-video__control-button--speed');

    // Play button click
    this.playButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.expand();
    });

    // Video click
    this.video.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.isExpanded) {
        this.expand();
      } else {
        this.togglePlayPause();
      }
    });

    // Close button (minimize)
    this.closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.minimize();
    });

    // Close hide button (add pi-hidden class)
    this.closeHideButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });

    // Replay button
    this.replayButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.replay();
    });

    // Volume button
    this.volumeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMute();
    });

    // Progress bar
    this.progress.addEventListener('click', (e) => {
      e.stopPropagation();
      const rect = this.progress.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      this.video.currentTime = percent * this.video.duration;
    });

    // Speed control
    let speeds = [1, 1.25, 1.5, 2];
    let speedIndex = 0;
    this.speedButton.addEventListener('click', (e) => {
      e.stopPropagation();
      speedIndex = (speedIndex + 1) % speeds.length;
      this.video.playbackRate = speeds[speedIndex];
      this.speedButton.textContent = speeds[speedIndex] + 'x';
    });

    // Time update
    this.video.addEventListener('timeupdate', () => {
      const percent = (this.video.currentTime / this.video.duration) * 100;
      this.progressBar.style.width = percent + '%';
      this.timestampCurrent.textContent = this.formatTime(this.video.currentTime);
    });

    // Duration loaded
    this.video.addEventListener('loadedmetadata', () => {
      this.timestampTotal.textContent = this.formatTime(this.video.duration);
    });

    // Stop propagation on container
    this.querySelector('.floating-video__container').addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }
  
  

  expand() {
    this.isExpanded = true;
    this.classList.remove('floating-video--minimized');
    this.classList.add('floating-video--expanded');
    if (!this.isPlaying) {
      this.video.play();
      this.isPlaying = true;
      this.classList.remove('pi-video-paused');
    }
    // Add click outside listener
    setTimeout(() => {
      document.addEventListener('click', this.handleClickOutside);
    }, 0);
  }

  minimize() {
    this.isExpanded = false;
    this.classList.remove('floating-video--expanded');
    this.classList.add('floating-video--minimized');
  
    // Mute if sound is enabled
    if (!this.isMuted) {
      this.toggleMute();
    }
  
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  }

  hide() {
    this.classList.add('pi-hidden');
    document.removeEventListener('click', this.handleClickOutside);
    // Store in session storage
    sessionStorage.setItem("floatingVideoHidden", "true");
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.video.pause();
      this.isPlaying = false;
      this.classList.add('pi-video-paused');
    } else {
      this.video.play();
      this.isPlaying = true;
      this.classList.remove('pi-video-paused');
    }
  }

  replay() {
    this.video.currentTime = 0;
    if (!this.isPlaying) {
      this.video.play();
      this.isPlaying = true;
      this.classList.remove('pi-video-paused');
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.video.muted = this.isMuted;
    if (this.isMuted) {
      this.volumeButton.classList.add('is-muted');
    } else {
      this.volumeButton.classList.remove('is-muted');
    }
  }

  handleClickOutside(e) {
    if (this.isExpanded && !this.contains(e.target)) {
      this.minimize();
    }
  }

  autoplay() {
    // Attempt autoplay with muted video
    setTimeout(() => {
      this.video.play().then(() => {
        this.isPlaying = true;
        this.classList.remove('pi-video-paused');
      }).catch(() => {
        // Autoplay blocked, user needs to interact
        this.isPlaying = false;
        this.classList.add('pi-video-paused');
      });
    }, 500);
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

customElements.define('floating-video-player', FloatingVideoPlayer);